import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Phone, Globe, Lock, BadgeCheck, Clock, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { activationRpcService } from "@/services/activationRpcService";
import { PriceLockButton } from "@/components/dealer-card/PriceLockButton";
import { useDealerCredits } from "@/hooks/useDealerCredits";

interface QuickActionsCardProps {
  phone?: string;
  website?: string;
  handlePriceLock: (metal: "ORO" | "ARGENTO") => void;
  handleActivateDealer: () => void;
  dealerActivated?: boolean;
  dealerId: string;
  citySlug: string;
  isAuthenticated?: boolean;
  // Props per i preferiti
  isFavorite?: boolean;
  handleToggleFavorite?: (e: React.MouseEvent) => void;
  isToggling?: boolean;
}

export function QuickActionsCard({
  phone,
  website,
  handlePriceLock,
  handleActivateDealer,
  dealerActivated = false,
  dealerId,
  citySlug,
  isAuthenticated = false,
  isFavorite = false,
  handleToggleFavorite,
  isToggling = false,
}: QuickActionsCardProps) {
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [isCheckingRequest, setIsCheckingRequest] = useState(true);
  const { canUsePriceLock, credits, isLoading: creditsLoading } = useDealerCredits(dealerId);
  
  // Verifica se esiste una richiesta di attivazione in corso
  useEffect(() => {
    if (!dealerActivated) {
      setIsCheckingRequest(true);
      activationRpcService.checkPendingRequest(dealerId)
        .then(hasPending => {
          setHasPendingRequest(hasPending);
        })
        .finally(() => {
          setIsCheckingRequest(false);
        });
    }
  }, [dealerId, dealerActivated]);
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Azioni Rapide</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {dealerActivated ? (
          // Azioni per dealer attivato
          <>
            {!canUsePriceLock && !creditsLoading && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                <div className="flex items-center gap-2 text-amber-800">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Blocca prezzo momentaneamente non attivo per questo compro oro
                  </span>
                </div>
                <p className="text-xs text-amber-700 mt-1">
                  Il commerciante ha terminato i crediti disponibili.
                </p>
              </div>
            )}
            
            <Button 
              onClick={(e) => {
                e.preventDefault();
                if (canUsePriceLock) {
                  handlePriceLock("ORO");
                }
              }} 
              className="w-full mb-2 bg-gold-light hover:bg-gold text-gold-dark"
              disabled={!canUsePriceLock || creditsLoading}
            >
              <Lock className="mr-2 h-4 w-4" />
              {canUsePriceLock ? "Blocca Prezzo Oro" : "Non Disponibile"}
            </Button>
            
            <Button 
              onClick={(e) => {
                e.preventDefault();
                if (canUsePriceLock) {
                  handlePriceLock("ARGENTO");
                }
              }} 
              className="w-full mb-4 bg-silver-light hover:bg-silver text-silver-dark"
              disabled={!canUsePriceLock || creditsLoading}
            >
              <Lock className="mr-2 h-4 w-4" />
              {canUsePriceLock ? "Blocca Prezzo Argento" : "Non Disponibile"}
            </Button>
          </>
        ) : hasPendingRequest ? (
          // Messaggio per richiesta in attesa
          <Button 
            variant="outline" 
            className="w-full mb-4 border-amber-300 bg-amber-50 text-amber-900 opacity-70 cursor-not-allowed"
            disabled
          >
            <Clock className="mr-2 h-4 w-4" />
            In attesa di verifica
          </Button>
        ) : (
          // Azione per attivare il dealer
          <Button 
            variant="outline" 
            className="w-full mb-4 border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900"
            onClick={(e) => {
              e.preventDefault();
              if (isAuthenticated) {
                handleActivateDealer();
              } else {
                window.location.href = "/auth/login";
              }
            }}
            disabled={isCheckingRequest}
          >
            {isCheckingRequest ? (
              <>
                <Clock className="mr-2 h-4 w-4 animate-spin" />
                Verifica stato...
              </>
            ) : (
              <>
                <BadgeCheck className="mr-2 h-4 w-4" />
                Richiedi Attivazione
              </>
            )}
          </Button>
        )}
        
        <div className="flex flex-col space-y-2">
          {/* Bottone Preferiti - PRIMO */}
          {handleToggleFavorite && (
            <Button 
              variant="outline" 
              className={`w-full justify-start ${
                isFavorite 
                  ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100' 
                  : 'hover:border-red-300 hover:bg-red-50 hover:text-red-700'
              }`}
              onClick={handleToggleFavorite}
              disabled={isToggling}
            >
              <Heart 
                className={`mr-2 h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} 
              />
              {isToggling 
                ? 'Aggiornamento...' 
                : isFavorite 
                  ? 'Rimuovi dai Preferiti' 
                  : 'Aggiungi ai Preferiti'
              }
            </Button>
          )}
          
          {phone && (
            <Button variant="outline" className="w-full justify-start" asChild>
              <a href={`tel:${phone}`} target="_blank" rel="noopener noreferrer">
                <Phone className="mr-2 h-4 w-4" />
                Chiama
              </a>
            </Button>
          )}
          
          {website && typeof website === 'string' && (
            <Button variant="outline" className="w-full justify-start" asChild>
              <a 
                href={website.startsWith('http') ? website : `https://${website}`}
                target="_blank" 
                rel={dealerActivated ? "noopener noreferrer" : "noopener noreferrer nofollow"}
              >
                <Globe className="mr-2 h-4 w-4" />
                Visita Sito Web
              </a>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
